// Phase 32 Plan 32-03 Task 2 — FlopReveal cinematic (RUNE-34).
//
// REWRITE: the prior Phase 17 placeholder body (1400ms auto-dismiss) is
// replaced by a useRevealFSM consumer driving the full 3000ms UI-SPEC §4
// cinematic with tap-to-skip after t=1500ms and reduced-motion 200ms
// cross-fade fallback (Pitfall 4 fork).
//
// Shape:
//   - DOM scrim (absolute, inset 0, zIndex 60) — pointerEvents flips from
//     'none' to 'auto' once fsm.skipEnabled === true so taps before t=1500ms
//     pass through to the canvas underneath.
//   - h1 "THE FLOP" + subtitle — locale keys reveal.t6.title / .subtitle.
//   - useAnnounce fires the polite SR announcement reveal_t6_flop on mount.
//   - $cascadeFrontier atom is mirrored from fsm.cascadeFrontier so the
//     LetterCubeSprite consumer (Plan 32-05) can gate spawn-in (Pitfall 3).
//
// What's NOT in FlopReveal anymore (legacy responsibilities pushed to
// useRevealFSM or sibling components):
//   - Per-component setTimeout for auto-dismiss (FSM owns the chain).
//   - Inline reduced-motion check (FSM owns the fork).
//   - Reading $reducedMotion directly (FSM exposes `reducedMotion`).
//   - The Phase 14 canvas-based RevealCinematic route — DELETED from disk
//     in this same plan (no more T1 / T12 paths to maintain).

import { type JSX, type KeyboardEvent, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { playSfx } from '../../game/audio/useAudio'
import { REVEAL_FALLBACK_STRINGS } from '../../game/reveals/timing'
import { pulseBoardReanchor } from '../../stores/board-reanchor'
import { $cascadeFrontier } from '../../stores/cascade-frontier'
import { useAnnounce } from '../components/useAnnounce'
import { useRevealFSM } from './useRevealFSM'

export interface FlopRevealProps {
  /** Cell indices that comprise this growth wave (T6: cells 36..47). The
   *  prop is informational — the cascade is driven by `$cascadeFrontier`
   *  (a count); the indices arrive here so future overlays (numbered
   *  callouts, slot-flipped highlight rings) can reach for the geometry. */
  readonly newCellIndices: ReadonlyArray<number>
  readonly onComplete: () => void
}

export function FlopReveal({
  newCellIndices: _newCellIndices,
  onComplete,
}: FlopRevealProps): JSX.Element {
  const intl = useIntl()
  const announce = useAnnounce()
  const fsm = useRevealFSM({ onComplete })

  // Mount-time polite SR announcement (POLISH-07 routing via global
  // <LiveRegion>; useAnnounce debounces by 100ms).
  useEffect(() => {
    announce(
      intl.formatMessage({ id: 'a11y.announce.reveal_t6_flop' }),
      'polite',
    )
  }, [announce, intl])

  // THE FLOP cue: layered drumroll build → trumpet stinger. Plays once on
  // mount regardless of reduced motion (audio is gated on mute only).
  // Phase 2 (court-opens-at-the-center): also fire the 'grow-impact' grow-thud
  // here on MOUNT — mount runs in BOTH motion modes (reduced + full), so the
  // thud is reduced-motion-safe. Deliberately NOT hooked to boardGrowReleased
  // (which never pulses under reduced motion) so the audio lands every time.
  useEffect(() => {
    playSfx('flop')
    playSfx('grow-impact')
  }, [])

  // Mirror the FSM's cascade frontier into the global atom so
  // LetterCubeSprite (Plan 32-05) can gate spawn-in for cells 36..47.
  // Reset to 0 on unmount so a future River reveal starts clean.
  useEffect(() => {
    $cascadeFrontier.set(fsm.cascadeFrontier)
  }, [fsm.cascadeFrontier])
  // Mirror the FSM's floor-drop release into $boardReanchor so existing
  // LetterCubeSprite cubes start their old→new anchor glide at the
  // floor-drop beat (~t=900ms), overlapping the new-cell cascade (Task 5).
  useEffect(() => {
    if (fsm.boardGrowReleased) pulseBoardReanchor()
  }, [fsm.boardGrowReleased])
  useEffect(() => {
    return () => {
      // Reset cascade frontier so the River reveal starts its own 0..12 count
      // from a clean baseline. Do NOT reset $boardReanchor here — the epoch
      // is monotonic across the full match and must not reset between reveals.
      $cascadeFrontier.set(0)
    }
  }, [])

  const title = intl.formatMessage({
    id: 'reveal.t6.title',
    defaultMessage: REVEAL_FALLBACK_STRINGS.t6.title,
  })
  const subtitle = intl.formatMessage({
    id: 'reveal.t6.subtitle',
    defaultMessage: REVEAL_FALLBACK_STRINGS.t6.subtitle,
  })

  const handleTap = (): void => {
    if (fsm.skipEnabled) fsm.dismiss()
  }
  const handleKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    // Native <button> already handles Enter / Space — but the cinematic is
    // typically full-screen + non-focused at mount, so we keep an explicit
    // handler for completeness and so the event semantics are testable.
    if (fsm.skipEnabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      fsm.dismiss()
    }
  }

  return (
    // The scrim wraps the canvas + display word. UI-SPEC §4 "dismissable"
    // state: when fsm.skipEnabled is true, taps + Enter / Space dismiss.
    // Use a native <button> so role + keyboard activation are intrinsic;
    // aria-label is the localized cinematic announce text.
    <button
      type="button"
      disabled={!fsm.skipEnabled}
      aria-label={intl.formatMessage({
        id: 'a11y.announce.reveal_t6_flop',
        defaultMessage: 'The flop. Twelve new runes.',
      })}
      data-reveal="flop"
      data-phase={fsm.phase}
      data-skip-enabled={fsm.skipEnabled ? 'true' : 'false'}
      data-reduced-motion={fsm.reducedMotion ? 'true' : 'false'}
      data-cascade-frontier={fsm.cascadeFrontier}
      onClick={handleTap}
      onKeyDown={handleKey}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        pointerEvents: fsm.skipEnabled ? 'auto' : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Reset button defaults so the scrim renders cleanly.
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: fsm.skipEnabled ? 'pointer' : 'default',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'inherit',
      }}
    >
      {/* Top scrim — Inkwell-tinted gradient over the chrome strip. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 100,
          background:
            'linear-gradient(180deg, var(--color-inkwell), transparent)',
          pointerEvents: 'none',
        }}
      />
      {/* Bottom scrim — taller so the display word reads bright over the
          lower half of the board. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 'auto 0 0 0',
          height: 280,
          background:
            'linear-gradient(0deg, var(--color-inkwell), transparent)',
          pointerEvents: 'none',
        }}
      />
      <h1
        className="display-word gradient"
        style={{
          position: 'relative',
          textAlign: 'center',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-52)',
          letterSpacing: '-0.02em',
          margin: 0,
          // Sun-Yellow accent flash (UI-SPEC §4 "River vs Flop" table) on
          // the FLOP variant — the reduced-motion path keeps static α 1.0.
          color: 'var(--color-cta-primary)',
        }}
      >
        {title}
      </h1>
      <span
        style={{
          position: 'relative',
          textAlign: 'center',
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-14)',
          fontStyle: 'italic',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--space-4)',
          letterSpacing: '0.04em',
        }}
      >
        {subtitle}
      </span>
    </button>
  )
}
